'use client'

import type {
  OptionalRestArgsOrSkip,
  PaginatedQueryArgs,
  PaginatedQueryReference,
  RequestForQueries,
  UsePaginatedQueryReturnType,
} from 'convex/react'
import type {
  FunctionArgs,
  FunctionReference,
  FunctionReturnType,
} from 'convex/server'
import { useMemo, useState } from 'react'
import {
  useAuthedPaginatedQuery,
  useAuthedQueries,
  useAuthedQuery,
  useSoftAuthedQuery,
} from '@/hooks/use-authed-query'
import {
  isSeedForArgs,
  type QuerySeed,
  type QuerySeeds,
} from '@/lib/data/convex/query-seed'

function isRetiredSeed<Seed extends object>(
  retired: ReadonlyArray<WeakRef<Seed>>,
  seed: Seed
) {
  return retired.some((reference) => reference.deref() === seed)
}

/** Immutable history tells React about retirement without retaining old payloads. */
function useRetiredSeeds<Seed extends object>(answered: ReadonlyArray<Seed>) {
  const [retired, setRetired] = useState<ReadonlyArray<WeakRef<Seed>>>([])
  const newAnswers = answered.filter((seed) => !isRetiredSeed(retired, seed))
  if (newAnswers.length > 0) {
    setRetired([
      ...retired.filter((reference) => reference.deref() !== undefined),
      ...newAnswers.map((seed) => new WeakRef(seed)),
    ])
  }
  return retired
}

/**
 * {@link useAuthedQuery} that paints a server seed until the live
 * subscription first answers, but only while the subscription's
 * args are the args the seed was read with — a changed filter, window or
 * record never shows another request's data.
 *
 * With the seed built by `settleSeed` around a prefetchable reader, a link
 * prefetch carries it, so the page's data is in the first frame of the
 * navigation instead of a skeleton waiting on the websocket.
 */
export function useSeededAuthedQuery<Query extends FunctionReference<'query'>>(
  query: Query,
  args: FunctionArgs<Query> | 'skip',
  seed: QuerySeed<Query> | null | undefined
): FunctionReturnType<Query> | undefined {
  const live = useAuthedQuery(
    query,
    ...([args] as OptionalRestArgsOrSkip<Query>)
  )
  return useQuerySeed(args, seed, live, false)
}

/** Fail-soft shell reads refuse and retire their seed on a live lookup error. */
export function useSoftSeededAuthedQuery<
  Query extends FunctionReference<'query'>,
>(
  query: Query,
  args: FunctionArgs<Query> | 'skip',
  seed: QuerySeed<Query> | null | undefined
) {
  const live = useSoftAuthedQuery(
    query,
    ...([args] as OptionalRestArgsOrSkip<Query>)
  )
  return {
    data: useQuerySeed(args, seed, live.data, live.isError),
    isError: live.isError,
  }
}

function useQuerySeed<Query extends FunctionReference<'query'>>(
  args: FunctionArgs<Query> | 'skip',
  seed: QuerySeed<Query> | null | undefined,
  live: FunctionReturnType<Query> | undefined,
  isError: boolean
): FunctionReturnType<Query> | undefined {
  const matches = seed && args !== 'skip' && isSeedForArgs(seed, args)
  const retired = useRetiredSeeds(
    matches && (live !== undefined || isError) ? [seed] : []
  )
  if (isError) {
    return
  }
  if (live !== undefined) {
    return live
  }
  if (!matches || isRetiredSeed(retired, seed)) {
    return
  }
  return seed.value
}

/**
 * {@link useAuthedQueries} that paints server seeds per request key: each
 * key answers `live[key] ?? seeds[key]`, the seed only while that request's
 * args are the args it was read with. A batch whose ids changed waits for
 * its own subscription; the batches that did not change keep their seed.
 */
export function useSeededAuthedQueries(
  queries: RequestForQueries,
  seeds: QuerySeeds | null | undefined
): Record<string, unknown | undefined> {
  const live = useAuthedQueries(queries)
  const answered: Array<QuerySeeds[string]> = []
  for (const [key, request] of Object.entries(queries)) {
    const seed = seeds?.[key]
    if (seed && live[key] !== undefined && isSeedForArgs(seed, request.args)) {
      answered.push(seed)
    }
  }
  const retired = useRetiredSeeds(answered)
  return useMemo(() => {
    if (!seeds) {
      return live
    }
    let merged: Record<string, unknown | undefined> | undefined
    for (const [key, request] of Object.entries(queries)) {
      const seed = seeds[key]
      if (
        live[key] === undefined &&
        seed !== undefined &&
        !isRetiredSeed(retired, seed) &&
        isSeedForArgs(seed, request.args)
      ) {
        merged ??= { ...live }
        merged[key] = seed.value
      }
    }
    return merged ?? live
  }, [live, queries, seeds, retired])
}

/**
 * {@link useAuthedPaginatedQuery} that paints a server-read FIRST page until
 * the live subscription answers. The seed must be read with the same args and
 * `paginationOpts: { numItems: initialNumItems, cursor: null }` (see
 * `settleSeed`); any other args — a filter, another page size — never
 * show it. Until the subscription lands the seeded page reports
 * `LoadingMore` (not `CanLoadMore`), so no "load more" control is offered
 * against a query that cannot page yet.
 */
export function useSeededAuthedPaginatedQuery<
  Query extends PaginatedQueryReference,
>(
  query: Query,
  args: PaginatedQueryArgs<Query> | 'skip',
  options: Parameters<typeof useAuthedPaginatedQuery<Query>>[2],
  seed: QuerySeed<Query> | null | undefined
): UsePaginatedQueryReturnType<Query> {
  const live = useAuthedPaginatedQuery(query, args, options)
  const seedArgs = {
    ...(args === 'skip' ? {} : args),
    paginationOpts: { numItems: options.initialNumItems, cursor: null },
  } as FunctionArgs<Query>
  const matches = seed && args !== 'skip' && isSeedForArgs(seed, seedArgs)
  const retired = useRetiredSeeds(
    matches && live.status !== 'LoadingFirstPage' ? [seed] : []
  )
  if (!matches) {
    return live
  }
  if (live.status !== 'LoadingFirstPage') {
    return live
  }
  if (isRetiredSeed(retired, seed)) {
    return live
  }
  return {
    ...live,
    results: seed.value.page,
    status: seed.value.isDone ? 'Exhausted' : 'LoadingMore',
    isLoading: !seed.value.isDone,
  } as UsePaginatedQueryReturnType<Query>
}
